<script lang="ts" setup>
import { ref, onMounted, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import CarretsBlack from '@icons/carrets-black.svg';
const consent = ref({
  necessary: true,
  analytics: true,
  preferences: true,
  marketing: false,
});

const showConsentBanner = ref(true);

function acceptAll() {

  updateConsent({
    necessary: true,
    analytics: true,
    preferences: true,
    marketing: true,
  });
}

function acceptSome() {
  updateConsent({
    necessary: true,
    analytics: consent.value.analytics,
    preferences: consent.value.preferences,
    marketing: consent.value.marketing,
  });
}

function rejectAll() {
  updateConsent({
    necessary: false,
    analytics: false,
    preferences: false,
    marketing: false,
  });
}

function updateConsent(updatedConsent: Record<string, boolean>) {
    const consentMode = {
        functionality_storage: updatedConsent.necessary ? 'granted' : 'denied',
        security_storage: updatedConsent.necessary ? 'granted' : 'denied',
        ad_storage: updatedConsent.marketing ? 'granted' : 'denied',
        ad_user_data: updatedConsent.marketing ? 'granted' : 'denied',
        analytics_storage: updatedConsent.analytics ? 'granted' : 'denied',
        personalization: updatedConsent.preferences ? 'granted' : 'denied',
        ad_personalization: updatedConsent.preferences ? 'granted' : 'denied',
    };
    gtag('consent', 'update', consentMode);
    setItemWithExpiry('consentMode', JSON.stringify(consentMode), 395);
    showConsentBanner.value = false;
}

function setItemWithExpiry(key: string, value: string, expiryDays: number) {
    const now = new Date();
    const item = {
        value: value,
        expiry: now.getTime() + expiryDays * 24 * 60 * 60 * 1000,
    };
    localStorage.setItem(key, JSON.stringify(item));
}

function getItemWithExpiry(key: string) {
  const itemString = localStorage.getItem(key);
  if (!itemString) {
    return null;
  }
  const item = JSON.parse(itemString);
  const now = new Date();
  if (now.getTime() > item.expiry) {    
    localStorage.removeItem(key);
    return null;
  }
  return item.value;
}

onMounted(() => {
  if (getItemWithExpiry('consentMode') !== null) {
    showConsentBanner.value = false;
  }

  watch(showConsentBanner, 
   useDebounceFn((newVal) => {
    let chaportIntegration = document.getElementsByClassName('chaport-launcher-button');

    if (chaportIntegration.length > 0) {
      if (newVal) {
        chaportIntegration[0].classList.add('hidden');
      } else {  
        chaportIntegration[0].classList.remove('hidden'); 
      }
    }  
  }, 1000), { immediate: true });
});


</script>

<template>
    <PrimeDrawer v-model:visible="showConsentBanner" 
        position="bottom" 
        :modal="false" 
        :dismissable="false" 
        :autoZIndex="false"
        :showCloseIcon="false"
        :pt="{ mask: { class:'google-consent-popup-mask', style: 'top:unset;bottom:0;height:auto;'}}"
    >
    <div class="consent-banner-content">
        <div class="consent-banner-wrapper">
                <h3>Cookie settings</h3>
                <p>
                We use cookies to provide you with the best possible experience. They also allow us to analyze user behavior in order to constantly improve the website for you.
                </p>
                <div class="consent-buttons">
                    <button as="button" class="secondary small" aria-label="Reject All" @click="rejectAll">
                        Reject All
                        <div aria-label="hidden" class="icon">
                            <CarretsBlack alt="arrow right"/>
                        </div>
                    </button>
                    <button as="button" class="secondary small" aria-label="Accept Selection" @click="acceptSome">
                        Accept Selection
                        <div aria-label="hidden" class="icon">
                            <CarretsBlack alt="arrow right"/>
                        </div>
                    </button>
                    <button as="button" class="primary small" aria-label="Accept All" @click="acceptAll">
                        Accept All
                        <div aria-label="hidden" class="icon">
                            <CarretsBlack alt="arrow right"/>
                        </div>
                    </button>
                </div>
                <div class="cookie-consent-options">
                    <label>
                        <PrimeCheckbox binary disabled value="Necessary" v-model="consent.necessary" /> 
                        Necessary
                    </label>
                    <label>          
                        <PrimeCheckbox binary :unstyled="true"  value="Analytics" v-model="consent.analytics"/>
                        Analytics
                    </label>  
                    <label>   
                        <PrimeCheckbox binary unstyled value="Preferences" v-model="consent.preferences"/>
                        Preferences
                    </label>
                    <label>
                        <PrimeCheckbox binary :unstyled="true" value="Marketing" v-model="consent.marketing"/>
                        Marketing
                    </label>
                </div>
        </div>
    </div>
    </PrimeDrawer>
</template>